import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Button, EmptyState, Field, Select, Spinner } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import type { Warehouse } from '../lib/types';
import type { StockReport } from '../lib/types-faz8';
import { Figure, Section, Table, td, tdR, th, thR } from '../components/reports/blocks';
import { downloadReport } from '../components/reports/download';
import { PeriodControls, usePeriod } from '../components/reports/period';
import { PrintButton, PrintHeader } from '../components/reports/print';

/**
 * Stok raporu (F8-07, ADR-806): stok değeri (güncel maliyet), depo ve kategori kırılımı, SKT ve kritik stok özeti,
 * dönem devir hızı, varyant tablosu, yavaş hareket edenler. Tanımlar: docs/faz-8/REPORTS_FINANCE_TR.md#stok-raporu.
 */

const SLOW_DAYS = [30, 60, 90, 180, 365] as const;
type Row = StockReport['variants']['rows'][number];

/** Bütün stok maliyetsizse değer bilinmiyor ("—"); sıfır yazılmaz. */
function useValueLabel() {
  const money = useMoney();
  return (value: number | null, units: number, unknownUnits: number) =>
    value === null || (units > 0 && unknownUnits >= units) ? '—' : money(value, 'TRY');
}

export function StockReportPage() {
  const t = useT();
  const money = useMoney();
  const { formatNumber } = useI18n();
  const period = usePeriod();
  const [warehouseId, setWarehouseId] = useState('');
  const [slowDays, setSlowDays] = useState('90');
  const warehouses = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => api.get<Warehouse[]>('/v1/admin/warehouses'),
  });
  const params = new URLSearchParams({ from: period.from, to: period.to, slowDays });
  if (warehouseId) params.set('warehouseId', warehouseId);
  const q = useQuery({
    queryKey: ['stock-report', params.toString()],
    queryFn: () => api.get<StockReport>(`/v1/admin/reports/stock?${params}`),
    placeholderData: keepPreviousData,
  });
  const tl = (n: number | null) => money(n, 'TRY');
  const n0 = (n: number) => formatNumber(n, 0);
  const valueLabel = useValueLabel();
  const d = q.data;
  return (
    <PageFrame
      helpKey="page.stock_report"
      printable
      actions={
        <span className="inline-flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => void downloadReport(`/v1/admin/reports/stock?${params}&format=csv`, 'stok.csv')}
          >
            {t('admin.reports.csv')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void downloadReport(`/v1/admin/reports/stock?${params}&format=xlsx`, 'stok.xlsx')}
          >
            {t('admin.reports.xlsx')}
          </Button>
          <HelpTip k="reports.download" />
          <PrintButton />
        </span>
      }
    >
      <PrintHeader title={t('admin.stockReport.navLabel')} period={period} />
      <div className="flex flex-wrap items-end gap-4 print:hidden">
        <PeriodControls period={period} showInterval={false} />
        <Field label={t('admin.stockReport.warehouse')} help={<HelpTip k="stock_report.warehouse" />}>
          {(p) => (
            <Select {...p} value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
              <option value="">{t('admin.stockReport.allWarehouses')}</option>
              {(warehouses.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.stockReport.slowDays')} help={<HelpTip k="stock_report.slow" />}>
          {(p) => (
            <Select {...p} value={slowDays} onChange={(e) => setSlowDays(e.target.value)}>
              {SLOW_DAYS.map((x) => (
                <option key={x} value={String(x)}>
                  {t('admin.stockReport.slowDaysOption', { days: x })}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      {q.isError ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : String(q.error)}
        </p>
      ) : !d ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <div className={q.isPlaceholderData ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <div className="max-w-[90ch] space-y-1 text-sm text-ink-3">
            <p>{d.costVisible ? t('admin.stockReport.method') : t('admin.stockReport.costHidden')}</p>
            {d.period.partial && <p>{t('admin.reports.partial')}</p>}
            {d.totals.negativeUnits > 0 && (
              <p role="status" className="text-amber">
                {t('admin.stockReport.negative', { count: n0(d.totals.negativeUnits) })}
              </p>
            )}
            {d.costVisible && d.totals.fxMissing > 0 && (
              <p role="alert" className="text-amber">
                {t('admin.stockReport.fxMissing', { count: n0(d.totals.fxMissing) })}
              </p>
            )}
          </div>

          <section
            aria-label={t('admin.stockReport.tilesTitle')}
            className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5"
          >
            {d.costVisible && (
              <Figure
                label={t('admin.stockReport.tiles.value')}
                value={valueLabel(d.totals.value, d.totals.units, d.totals.unknownCostUnits)}
                sub={
                  d.totals.unknownCostUnits > 0
                    ? t('admin.stockReport.unknownCostSub', { count: n0(d.totals.unknownCostUnits) })
                    : t('admin.stockReport.valueSub')
                }
                help={<HelpTip k="stock_report.value" />}
              />
            )}
            <Figure
              label={t('admin.stockReport.tiles.units')}
              value={n0(d.totals.units)}
              sub={t('admin.stockReport.unitsSub', { count: n0(d.totals.variantsInStock) })}
            />
            <Figure
              label={t('admin.stockReport.tiles.turnover')}
              value={
                d.turnover.turnoverCenti === null
                  ? '—'
                  : t('admin.stockReport.turnoverValue', {
                      x: formatNumber(d.turnover.turnoverCenti / 100, 2),
                    })
              }
              sub={t('admin.stockReport.turnoverSub', {
                sold: n0(d.turnover.soldUnits),
                avg: n0(d.turnover.averageUnits),
              })}
              help={<HelpTip k="stock_report.turnover" />}
            />
            <Figure
              label={t('admin.stockReport.tiles.daysOfInventory')}
              value={
                d.turnover.daysOfInventory === null
                  ? '—'
                  : t('admin.stockReport.daysValue', { days: n0(d.turnover.daysOfInventory) })
              }
              sub={d.turnover.daysOfInventory === null ? t('admin.stockReport.noSales') : undefined}
            />
            <Figure
              label={t('admin.stockReport.tiles.slow')}
              value={d.costVisible ? tl(d.totals.slow.value) : n0(d.totals.slow.units)}
              sub={t('admin.stockReport.slowSub', {
                count: n0(d.totals.slow.variants),
                units: n0(d.totals.slow.units),
              })}
              help={<HelpTip k="stock_report.slow" />}
            />
          </section>

          <Alerts d={d} />

          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <WarehousesTable d={d} />
            <CategoriesTable d={d} />
          </div>

          <div className="mt-8">
            <Section title={t('admin.stockReport.slowTitle')} help={<HelpTip k="stock_report.slow" />}>
              <p className="mb-2 text-sm text-ink-2">
                {t('admin.stockReport.slowLead', { days: d.totals.slow.days })}
              </p>
              {!d.slow.rows.length ? (
                <EmptyState title={t('admin.stockReport.slowNone')} />
              ) : (
                <VariantTable
                  rows={d.slow.rows}
                  total={d.slow.total}
                  caption={t('admin.stockReport.slowTitle')}
                  costVisible={d.costVisible}
                />
              )}
            </Section>
          </div>

          <div className="mt-8">
            <Section
              title={t('admin.stockReport.variantsTitle')}
              help={<HelpTip k="stock_report.turnover" />}
            >
              {!d.variants.rows.length ? (
                <EmptyState title={t('admin.stockReport.empty')} />
              ) : (
                <VariantTable
                  rows={d.variants.rows}
                  total={d.variants.total}
                  caption={t('admin.stockReport.variantsTitle')}
                  costVisible={d.costVisible}
                />
              )}
            </Section>
          </div>
        </div>
      )}
    </PageFrame>
  );
}

function Alerts({ d }: { d: StockReport }) {
  const t = useT();
  const money = useMoney();
  const { formatNumber } = useI18n();
  const items: Array<{ key: string; label: string; amount: string; value: string | null }> = [
    {
      key: 'expired',
      label: t('admin.stockReport.alerts.expired'),
      amount: t('admin.stockReport.alertUnits', { units: formatNumber(d.totals.expired.units, 0) }),
      value: d.costVisible ? money(d.totals.expired.value, 'TRY') : null,
    },
    {
      key: 'expiring',
      label: t('admin.stockReport.alerts.expiring', { days: d.totals.expiring.days }),
      amount: t('admin.stockReport.alertUnits', { units: formatNumber(d.totals.expiring.units, 0) }),
      value: d.costVisible ? money(d.totals.expiring.value, 'TRY') : null,
    },
    {
      key: 'critical',
      label: t('admin.stockReport.alerts.critical'),
      amount: t('admin.stockReport.alertVariants', { count: formatNumber(d.totals.critical, 0) }),
      value: null,
    },
  ];
  return (
    <div className="mt-8">
      <Section title={t('admin.stockReport.alertsTitle')}>
        <dl className="grid gap-3 sm:grid-cols-3">
          {items.map((x) => (
            <div key={x.key} className="rounded-md border border-line bg-surface p-3">
              <dt className="text-sm text-ink-3">{x.label}</dt>
              <dd className="mt-1 font-semibold text-ink">
                {x.amount}
                {x.value && <span className="ms-2 text-sm font-normal text-ink-2">{x.value}</span>}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-2 text-sm">
          <Link to="/stok" className="font-medium text-pine hover:underline">
            {t('admin.stockReport.goStock')}
          </Link>
        </p>
      </Section>
    </div>
  );
}

function WarehousesTable({ d }: { d: StockReport }) {
  const t = useT();
  const valueLabel = useValueLabel();
  const { formatNumber } = useI18n();
  return (
    <Section title={t('admin.stockReport.warehousesTitle')}>
      {!d.byWarehouse.length ? (
        <EmptyState title={t('admin.stockReport.empty')} />
      ) : (
        <Table
          caption={t('admin.stockReport.warehousesTitle')}
          head={
            <tr>
              <th scope="col" className={th}>
                {t('admin.stockReport.warehouseCols.warehouse')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.stockReport.warehouseCols.variants')}
              </th>
              <th scope="col" className={thR}>
                {t('admin.stockReport.warehouseCols.units')}
              </th>
              {d.costVisible && (
                <>
                  <th scope="col" className={thR}>
                    {t('admin.stockReport.warehouseCols.value')}
                  </th>
                  <th scope="col" className={thR}>
                    {t('admin.stockReport.warehouseCols.unknown')}
                  </th>
                </>
              )}
            </tr>
          }
        >
          {d.byWarehouse.map((w) => (
            <tr key={w.warehouseId} className="border-t border-line">
              <th scope="row" className={`${td} text-start font-normal`}>
                {w.name}
                {!w.active && (
                  <span className="ms-1 text-xs text-ink-3">({t('admin.stockReport.inactive')})</span>
                )}
              </th>
              <td className={tdR}>{formatNumber(w.variants, 0)}</td>
              <td className={tdR}>{formatNumber(w.units, 0)}</td>
              {d.costVisible && (
                <>
                  <td className={tdR}>{valueLabel(w.value, w.units, w.unknownCostUnits)}</td>
                  <td className={tdR}>{formatNumber(w.unknownCostUnits, 0)}</td>
                </>
              )}
            </tr>
          ))}
        </Table>
      )}
    </Section>
  );
}

function CategoriesTable({ d }: { d: StockReport }) {
  const t = useT();
  const valueLabel = useValueLabel();
  const { formatNumber } = useI18n();
  return (
    <Section title={t('admin.stockReport.categoriesTitle')}>
      {!d.byCategory.length ? (
        <EmptyState title={t('admin.stockReport.empty')} />
      ) : (
        <>
          <Table
            caption={t('admin.stockReport.categoriesTitle')}
            head={
              <tr>
                <th scope="col" className={th}>
                  {t('admin.stockReport.categoryCols.category')}
                </th>
                <th scope="col" className={thR}>
                  {t('admin.stockReport.categoryCols.units')}
                </th>
                {d.costVisible && (
                  <th scope="col" className={thR}>
                    {t('admin.stockReport.categoryCols.value')}
                  </th>
                )}
                <th scope="col" className={thR}>
                  {t('admin.stockReport.categoryCols.sold')}
                </th>
              </tr>
            }
          >
            {d.byCategory.map((c) => (
              <tr key={c.key} className="border-t border-line">
                <th scope="row" className={`${td} text-start font-normal`}>
                  {c.label ?? <span className="text-ink-3">{t('admin.stockReport.uncategorized')}</span>}
                </th>
                <td className={tdR}>{formatNumber(c.units, 0)}</td>
                {d.costVisible && <td className={tdR}>{valueLabel(c.value, c.units, c.unknownCostUnits)}</td>}
                <td className={tdR}>{formatNumber(c.soldUnits, 0)}</td>
              </tr>
            ))}
          </Table>
          <p className="mt-2 text-xs text-ink-3">{t('admin.stockReport.categoryNote')}</p>
        </>
      )}
    </Section>
  );
}

function VariantTable({
  rows,
  total,
  caption,
  costVisible,
}: {
  rows: Row[];
  total: number;
  caption: string;
  costVisible: boolean;
}) {
  const t = useT();
  const money = useMoney();
  const { formatNumber, formatDate } = useI18n();
  const cols = [
    'product',
    'onHand',
    'available',
    ...(costVisible ? (['unitCost', 'value'] as const) : []),
    'sold',
    'turnover',
    'cover',
    'lastSale',
  ] as const;
  return (
    <>
      {/* Kaydırılan bölge klavyeyle odaklanabilir (WCAG 2.1.1). */}
      <div className="max-h-[32rem] overflow-y-auto" tabIndex={0} role="region" aria-label={caption}>
        <Table
          caption={caption}
          head={
            <tr>
              {cols.map((k) => (
                <th key={k} scope="col" className={k === 'product' ? th : thR}>
                  {t(`admin.stockReport.variantCols.${k}`)}
                </th>
              ))}
            </tr>
          }
        >
          {rows.map((r) => (
            <tr key={r.variantId} className="border-t border-line">
              <th scope="row" className={`${td} text-start font-normal`}>
                <Link
                  to="/urunler/$id"
                  params={{ id: r.productId }}
                  className="font-medium text-pine hover:underline"
                >
                  {r.title}
                </Link>
                <span className="block text-xs text-ink-3">{r.sku}</span>
              </th>
              <td className={tdR}>{formatNumber(r.onHand, 0)}</td>
              <td className={tdR}>{formatNumber(r.available, 0)}</td>
              {costVisible && (
                <>
                  <td className={tdR}>
                    {r.unitCost === null ? (
                      <span className="text-ink-3">{t('admin.stockReport.noCost')}</span>
                    ) : (
                      money(r.unitCost, 'TRY')
                    )}
                  </td>
                  <td className={tdR}>{r.value === null ? '—' : money(r.value, 'TRY')}</td>
                </>
              )}
              <td className={tdR}>{formatNumber(r.soldUnits, 0)}</td>
              <td className={tdR}>
                {r.turnoverCenti === null ? '—' : formatNumber(r.turnoverCenti / 100, 2)}
              </td>
              <td className={tdR}>
                {r.daysOfCover === null
                  ? '—'
                  : t('admin.stockReport.daysValue', { days: formatNumber(r.daysOfCover, 0) })}
              </td>
              <td className={`${tdR} whitespace-nowrap`}>
                {r.lastSaleAt ? (
                  formatDate(r.lastSaleAt, false)
                ) : (
                  <span className="text-ink-3">{t('admin.stockReport.never')}</span>
                )}
              </td>
            </tr>
          ))}
        </Table>
      </div>
      {total > rows.length && (
        <p className="mt-2 text-xs text-ink-3">
          {t('admin.stockReport.showing', {
            shown: formatNumber(rows.length, 0),
            total: formatNumber(total, 0),
          })}
        </p>
      )}
    </>
  );
}
